<script lang="ts">
import {Button} from "@/components/ui/button/index.js";
import {Download, FileText, RotateCw, Trash2, ZoomIn, ZoomOut, PenLine, Clock, User, CheckCircle2, AlertCircle, Mail, Loader2} from "lucide-svelte";
import type {Document, Block, Field, Signature} from "@/types/types";
import {toast} from "svelte-sonner";
import {router, useForm, page} from "@inertiajs/svelte";
import InlineEditable from "@/components/Inputs/InlineEditable.svelte";
import FormContainer from "@/components/Form/FormContainer.svelte";
import FormBlock from "@/components/Form/FormBlock.svelte";
import { untrack } from 'svelte';
import Debug from '@/components/Debug.svelte';
import { formatDateLocale } from '@/lib/dateUtils';
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import * as Select from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import axios from "axios";

type props = {
    document: Document;
    blocks?: Block[];
}

let {document, blocks = []}: props = $props();

// Track signatures in local state so we can update immediately on delete
let localSignatures = $state<Signature[]>(document.signatures ?? []);

// Update local signatures when document changes
$effect(() => {
    localSignatures = document.signatures ?? [];
});

let hiddenFields = ['filename', 'related_record'];

// Create form data from blocks - this needs to run before form creation
let inlineFormInner: Record<string, any> = {};
for (const block of blocks) {
    for (const field of block.fields) {
        if (hiddenFields.includes(field.column_name)) continue;
        const fieldValue = field.value ?? document[field.column_name];
        inlineFormInner[field.column_name] = fieldValue;
    }
}

let inlineForm = $state(useForm(inlineFormInner));

// Create reactive local blocks with updated values
let localBlocks = $derived.by(() => {
    return blocks.map(block => ({
        ...block,
        fields: block.fields.map(field => {
            if (hiddenFields.includes(field.column_name)) {
                return field;
            }

            // Get value from form data or document
            const fieldValue = $inlineForm[field.column_name] ?? document[field.column_name] ?? field.value;

            return {
                ...field,
                value: fieldValue
            };
        })
    }));
});

// Separate editing state for this component (not shared with parent)
let editingFieldId = $state<number | undefined>(undefined);

// internal state
let previewUrl = $state('')
let previewLoaded = false

async function loadPreview() {
    const url = `/preview/${document.id}`

    try {
        // HEAD is enough, but GET also works if server doesn’t support HEAD
        const res = await fetch(url, { method: 'HEAD' })
        previewUrl = url
        if (!res.ok) {
            toast.error(`Document not found (Error ${res.status})`)
            return
        }
    } catch (err) {
        previewUrl = url
        toast.error(`Error loading document: ${err.message}`)
    }
}

$effect(() => {
    if (!previewLoaded && document?.id) {
        loadPreview()
        previewLoaded = true
    }
})

// Preview state
let previewZoom = $state(100);
let previewRotation = $state(0);
let showPreview = $state(true);

let previewHeight = $derived(`${Math.max(584, 584 * (previewZoom / 100))}px`);

// Preview functions
function zoomIn() {
    previewZoom = Math.min(previewZoom + 25, 200);
}

function zoomOut() {
    previewZoom = Math.max(previewZoom - 25, 25);
}

function rotate() {
    previewRotation = (previewRotation + 90) % 360;
}


// Get file extension for preview type detection
function getFileExtension(filename: string): string {
    return filename.split('.').pop()?.toLowerCase() || '';
}

function isPreviewable(filename: string): boolean {
    const ext = getFileExtension(filename);
    return ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'txt', 'html'].includes(ext);
}

function getPreviewType(filename: string): 'pdf' | 'image' | 'text' | 'unsupported' {
    const ext = getFileExtension(filename);
    if (ext === 'pdf') return 'pdf';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'image';
    if (['txt', 'html'].includes(ext)) return 'text';
    return 'unsupported';
}

// Delete document function
function deleteDocument() {
    if (confirm('Are you sure you want to delete this document?')) {
        router.delete(route('documents.destroy', {document: document.id}), {
            preserveScroll: true,
            onSuccess: () => {
                toast.success('Document deleted successfully');
            },
            onError: () => {
                toast.error('Failed to delete document');
            }
        });
    }
}

// Delete signature function
function deleteSignature(signatureId: string) {
    if (confirm('Are you sure you want to delete this signature?')) {
        router.delete(route('signatures.destroy', { signatureId }), {
            preserveScroll: true,
            onSuccess: () => {
                // Remove signature from local state immediately
                localSignatures = localSignatures.filter(s => s.id !== signatureId);
                toast.success('Signature deleted successfully');
            },
            onError: () => {
                toast.error('Failed to delete signature');
            }
        });
    }
}

// Email state
interface CustomerEmail {
    id: string;
    name: string;
    email: string;
}

let emailPopoverOpen = $state(false);
let customerEmails = $state<CustomerEmail[]>([]);
let loadingCustomerEmails = $state(false);
let selectedCustomerEmail = $state<string | undefined>(undefined);
let customEmail = $state('');
let emailMessage = $state('');
let sendingEmail = $state(false);

// Computed final email - use selected customer email or custom email
let finalEmail = $derived(selectedCustomerEmail || customEmail);

// Load customer emails when popover opens
async function loadCustomerEmails() {
    if (customerEmails.length > 0) return; // Already loaded

    loadingCustomerEmails = true;
    try {
        const response = await axios.get(route('documents.customer-emails', { document: document.id }));
        customerEmails = response.data.emails || [];
    } catch (error) {
        console.error('Failed to load customer emails', error);
    } finally {
        loadingCustomerEmails = false;
    }
}

function handleEmailPopoverOpen(open: boolean) {
    emailPopoverOpen = open;
    if (open) {
        loadCustomerEmails();
    }
}

async function sendDocumentEmail() {
    if (!finalEmail) {
        toast.error('Please enter an email address');
        return;
    }

    sendingEmail = true;
    try {
        const response = await axios.post(route('documents.send-email', { document: document.id }), {
            email: finalEmail,
            message: emailMessage || null,
        });

        if (response.data.success) {
            toast.success('Document sent successfully');
            emailPopoverOpen = false;
            // Reset form
            selectedCustomerEmail = undefined;
            customEmail = '';
            emailMessage = '';
        } else {
            toast.error(response.data.message || 'Failed to send email');
        }
    } catch (error: any) {
        toast.error(error.response?.data?.message || 'Failed to send email');
    } finally {
        sendingEmail = false;
    }
}

</script>

<!-- Document Preview Section -->
<div class="space-y-2">
    <div class="bg-white rounded-lg shadow-sm border flex flex-col">
        <!-- Preview Header -->
        <div class="flex items-center justify-between p-4 border-b">
            <div class="flex items-center gap-2">
                <FileText class="h-5 w-5" />
                <h3 class="font-semibold">Document Preview</h3>
                {#if document.title}
                    <span class="text-sm text-gray-500">({document.title})</span>
                {/if}
            </div>

            <!-- Preview Controls -->
            <div class="flex items-center gap-2">
                {#if document.filename && isPreviewable(document.filename || '')}
                    <Button variant="outline" size="sm" onclick={zoomOut} disabled={previewZoom <= 25}>
                        <ZoomOut class="h-4 w-4" />
                    </Button>
                    <span class="text-sm px-2">{previewZoom}%</span>
                    <Button variant="outline" size="sm" onclick={zoomIn} disabled={previewZoom >= 200}>
                        <ZoomIn class="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onclick={rotate}>
                        <RotateCw class="h-4 w-4" />
                    </Button>
                {/if}

                {#if document.filename}
                    <a href={route('documents.download', {documentId: document.id})} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" size="sm">
                            <Download class="h-4 w-4" />
                        </Button>
                    </a>

                    <!-- Email Document Button -->
                    <Popover bind:open={emailPopoverOpen} onOpenChange={handleEmailPopoverOpen}>
                        <PopoverTrigger>
                            <Button variant="outline" size="sm">
                                <Mail class="h-4 w-4" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent class="w-80" align="end">
                            <div class="space-y-4">
                                <div class="space-y-2">
                                    <h4 class="font-medium leading-none">Email Document</h4>
                                    <p class="text-sm text-muted-foreground">
                                        Send this document to an email address.
                                    </p>
                                </div>

                                <!-- Customer Email Select -->
                                {#if loadingCustomerEmails}
                                    <div class="flex items-center gap-2 text-sm text-muted-foreground">
                                        <Loader2 class="h-4 w-4 animate-spin" />
                                        Loading customers...
                                    </div>
                                {:else if customerEmails.length > 0}
                                    <div class="space-y-2">
                                        <Label>Select Customer</Label>
                                        <Select.Root
                                            type="single"
                                            bind:value={selectedCustomerEmail}
                                            onValueChange={(v) => {
                                                selectedCustomerEmail = v;
                                                if (v) customEmail = '';
                                            }}
                                        >
                                            <Select.Trigger class="w-full">
                                                {#snippet children({ open })}
                                                    {#if selectedCustomerEmail}
                                                        {customerEmails.find(c => c.email === selectedCustomerEmail)?.name ?? selectedCustomerEmail}
                                                    {:else}
                                                        <span class="text-muted-foreground">Select a customer...</span>
                                                    {/if}
                                                {/snippet}
                                            </Select.Trigger>
                                            <Select.Content>
                                                {#each customerEmails as customer (customer.id)}
                                                    <Select.Item value={customer.email}>
                                                        <span class="font-medium">{customer.name}</span>
                                                        <span class="text-xs text-muted-foreground ml-2">{customer.email}</span>
                                                    </Select.Item>
                                                {/each}
                                            </Select.Content>
                                        </Select.Root>
                                    </div>
                                    <div class="relative">
                                        <div class="absolute inset-0 flex items-center">
                                            <span class="w-full border-t" />
                                        </div>
                                        <div class="relative flex justify-center text-xs uppercase">
                                            <span class="bg-background px-2 text-muted-foreground">Or</span>
                                        </div>
                                    </div>
                                {/if}

                                <!-- Custom Email Input -->
                                <div class="space-y-2">
                                    <Label for="custom-email">Email Address</Label>
                                    <Input
                                        id="custom-email"
                                        type="email"
                                        placeholder="Enter email address"
                                        bind:value={customEmail}
                                        oninput={() => {
                                            if (customEmail) selectedCustomerEmail = undefined;
                                        }}
                                    />
                                </div>

                                <!-- Optional Message -->
                                <div class="space-y-2">
                                    <Label for="email-message">Message (optional)</Label>
                                    <Textarea
                                        id="email-message"
                                        placeholder="Add a message..."
                                        bind:value={emailMessage}
                                        rows={3}
                                    />
                                </div>

                                <!-- Send Button -->
                                <div class="flex justify-end gap-2">
                                    <Button variant="outline" size="sm" onclick={() => emailPopoverOpen = false}>
                                        Cancel
                                    </Button>
                                    <Button
                                        size="sm"
                                        onclick={sendDocumentEmail}
                                        disabled={!finalEmail || sendingEmail}
                                    >
                                        {#if sendingEmail}
                                            <Loader2 class="h-4 w-4 mr-2 animate-spin" />
                                            Sending...
                                        {:else}
                                            <Mail class="h-4 w-4 mr-2" />
                                            Send
                                        {/if}
                                    </Button>
                                </div>
                            </div>
                        </PopoverContent>
                    </Popover>
                {/if}

                <Button variant="outline" size="sm" onclick={deleteDocument}>
                    <Trash2 class="h-4 w-4" />
                </Button>
            </div>
        </div>

        <!-- Preview Content -->
        <div class="p-4  flex-1">
            {#if !document.filename}
                <div class="flex flex-col items-center justify-center py-12 text-gray-500">
                    <FileText class="h-12 w-12 mb-4" />
                    <p>No document file available</p>
                </div>
            {:else if !isPreviewable(document.filename || '')}
                <div class="flex flex-col items-center justify-center py-12 text-gray-500">
                    <FileText class="h-12 w-12 mb-4" />
                    <p>Preview not available for this file type</p>
                    <p class="text-sm">File: {document.filename}</p>
                    <a href={route('documents.download', {documentId: document.id})} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" size="sm">
                            <Download class="h-4 w-4" />
                        </Button>
                    </a>
                </div>
            {:else}
                <div class="preview-container overflow-auto border rounded" style="height: {previewHeight}">
                    {#if getPreviewType(document.filename || '') === 'pdf'}
                        <iframe
                            src={previewUrl}
                            class="w-full border-0 "
                            style="transform: scale({previewZoom / 100}) rotate({previewRotation}deg); transform-origin: top left; height: {previewHeight}"
                            title="Document Preview"
                        ></iframe>
                    {:else if getPreviewType(document.filename || '') === 'image'}
                        <div class="flex justify-center p-4 h-full">
                            <img
                                src={previewUrl}
                                alt="Document Preview"
                                class="max-w-full h-auto  max-h-full object-contain"
                                style="transform: scale({previewZoom / 100}) rotate({previewRotation}deg);"
                            />
                        </div>
                    {:else if getPreviewType(document.filename || '') === 'text'}
                        <div class="p-4 font-mono text-sm bg-gray-50 rounded" style="zoom: {previewZoom / 100};">
                            <!-- You'll need to fetch and display text content here -->
                            <p class="text-gray-500">Text preview would require fetching file content</p>
                        </div>
                    {/if}
                </div>
            {/if}
        </div>
    </div>

    <!-- Signatures Section -->
    {#if document.signature_required}
        <div class="bg-white rounded-lg shadow-sm border">
            <div class="flex items-center gap-2 p-4 border-b">
                <PenLine class="h-5 w-5" />
                <h3 class="font-semibold">Signatures</h3>
                {#if localSignatures.length > 0}
                    <span class="ml-auto flex items-center gap-1 text-sm text-green-600">
                        <CheckCircle2 class="h-4 w-4" />
                        {localSignatures.length} {localSignatures.length === 1 ? 'Signature' : 'Signatures'}
                    </span>
                {:else}
                    <span class="ml-auto flex items-center gap-1 text-sm text-amber-600">
                        <AlertCircle class="h-4 w-4" />
                        Awaiting Signatures
                    </span>
                {/if}
            </div>
            <div class="p-4">
                {#if localSignatures.length > 0}
                    <div class="space-y-4">
                        {#each localSignatures as signature (signature.id)}
                            <div class="flex items-start gap-4 p-3 bg-gray-50 rounded-lg">
                                {#if signature.signature_path}
                                    <img
                                        src={route('signatures.show', { signatureId: signature.id })}
                                        alt="Signature by {signature.signed_by_name}"
                                        class="h-16 border rounded bg-white p-1"
                                    />
                                {/if}
                                <div class="flex-1 space-y-1 text-sm">
                                    <div class="flex items-center gap-2">
                                        <User class="h-4 w-4 text-gray-500" />
                                        <span class="font-medium">{signature.signed_by_name || 'Unknown'}</span>
                                    </div>
                                    <div class="flex items-center gap-2 text-gray-500">
                                        <Clock class="h-4 w-4" />
                                        <span>{formatDateLocale(signature.signed_at)}</span>
                                    </div>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    class="text-destructive hover:text-destructive hover:bg-destructive/10"
                                    onclick={() => deleteSignature(signature.id)}
                                >
                                    <Trash2 class="h-4 w-4" />
                                </Button>
                            </div>
                        {/each}
                    </div>
                {:else}
                    <div class="flex flex-col items-center justify-center py-6 text-gray-500">
                        <PenLine class="h-10 w-10 mb-2 text-gray-400" />
                        <p>This document requires signatures</p>
                        <p class="text-sm">Waiting for signatures...</p>
                    </div>
                {/if}
            </div>
        </div>
    {/if}

    <!-- Document Fields -->
    <FormContainer>
        {#each localBlocks as block (block.id)}
            <FormBlock label={block.label}>
                {#each block.fields as field (field.id)}
                    {#if !hiddenFields.includes(field.column_name)}
                    <InlineEditable
                        {field}
                        bind:editingFieldId
                        error={$inlineForm.errors[field.column_name]}
                        onSave={(newValue) => {
                            $inlineForm[field.column_name] = newValue;
                            $inlineForm.put(route("documents.update", { document }), {
                                preserveScroll: true,
                                onSuccess: () => {
                                    editingFieldId = undefined;
                                    toast.success('Document updated')

                                },
                                onError: () => {
                                    toast.error('Failed to update document');
                                }
                            });
                        }}
                        record={document}
                    />
                        {/if}
                {/each}
            </FormBlock>
        {/each}
    </FormContainer>
</div>
